/**
 * Das Pendel — die Signature der App.
 *
 * Novelle: der Schlagzeitpunkt ist öffentlich. Der Countdown zählt sichtbar
 * herunter, und das Pendel glimmt stärker, je näher der Schlag rückt.
 * Zufällig bleibt allein die Ziehung der Verzögerung auf dem Server.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { formatEinheiten } from '../api/format.js'
import { aktuellesThema } from '../api/thema.js'
import type { SchnapsuhrStand } from '../api/typen.js'

/** Fortschritt bis zum Schlag: 0 beim Stellen, 1 im Moment der Fälligkeit. */
function fortschritt(jetztMs: number, abMs: number, faelligMs: number): number {
  if (faelligMs <= abMs) return 1
  return Math.min(1, Math.max(0, (jetztMs - abMs) / (faelligMs - abMs)))
}

export function Pendel({ glut, stehend = false }: { glut: number; stehend?: boolean }) {
  return (
    <svg
      width="88"
      height="150"
      viewBox="0 0 88 150"
      role="img"
      aria-label={`Die Schnapsuhr läuft. Anspannung ${Math.round(glut * 100)} von 100.`}
      className="pendel"
      style={{ '--glut': glut, animationPlayState: stehend ? 'paused' : undefined } as CSSProperties}
    >
      <circle cx="44" cy="4" r="3.5" className="pendel-gewicht" />
      <line x1="44" y1="6" x2="44" y2="112" className="pendel-stange" />
      <ellipse cx="44" cy="124" rx="18" ry="17" className="pendel-gewicht" />
      <ellipse cx="44" cy="124" rx="11" ry="10.5" fill="none" stroke="#241416" strokeWidth="1" opacity="0.45" />
      <path
        d="M 33 116 A 15 14 0 0 1 45 110"
        fill="none"
        stroke="#f3d9a4"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.65"
      />
    </svg>
  )
}

/**
 * Der Fristenzettel — das Gegenstück zum Pendel im Kanzlei-Gewand.
 *
 * Die Kanzlei lässt nichts schwingen; sie stempelt. Je näher die Frist rückt,
 * desto satter presst sich der Stempel ins Papier.
 */
function Fristenzettel({ glut }: { glut: number }) {
  return (
    <div
      className="fristenzettel mt-1"
      role="img"
      aria-label={`Fristenzettel. Frist zu ${Math.round(glut * 100)} von 100 abgelaufen.`}
      style={{ '--glut': glut } as CSSProperties}
    >
      <span className="fristenzettel-stempel">
        Ausschank
        <br />
        angeordnet
      </span>
    </div>
  )
}

/** Das Wahrzeichen der laufenden Uhr — je nach Gewand Pendel oder Zettel. */
function UhrZeichen({ glut }: { glut: number }) {
  const thema = useMemo(() => aktuellesThema(), [])
  return thema === 'kanzlei' ? <Fristenzettel glut={glut} /> : <Pendel glut={glut} />
}

export function SchnapsuhrAnzeige({
  stand,
  umschalten,
}: {
  stand: SchnapsuhrStand
  /** Schaltet die Uhr dieser Sitzung an oder aus. */
  umschalten?: (an: boolean) => void
}) {
  const [jetzt, setJetzt] = useState(Date.now())

  useEffect(() => {
    const takt = setInterval(() => setJetzt(Date.now()), 1_000)
    return () => clearInterval(takt)
  }, [])

  if (!stand.schaltbar) return null

  // Abgeschaltet: nur der Schalter.
  if (!stand.aktiv) {
    return (
      <div className="flex flex-col items-center border border-eiche bg-bank px-4 py-4 text-center">
        <p className="protokoll">§10 · Schnapsuhr · abgeschaltet</p>
        {umschalten && (
          <button
            type="button"
            onClick={() => umschalten(true)}
            className="protokoll mt-2 normal-case underline decoration-messing/40 underline-offset-4 hover:text-messing"
          >
            Einschalten
          </button>
        )}
      </div>
    )
  }

  const runde = stand.laufendeRunde
  if (!runde) {
    return (
      <div className="flex flex-col items-center border border-eiche bg-bank px-4 py-5 text-center">
        <p className="protokoll">§10 · Schnapsuhr</p>
        <p className="mt-2 text-sm text-akte/70">
          {stand.vergangeneRunden >= stand.maxRounds
            ? `Alle ${stand.maxRounds} Runden vollstreckt. Die Uhr schweigt.`
            : 'Die Uhr ist noch nicht gestellt. Sie beginnt mit dem ersten Check-in.'}
        </p>
        {umschalten && (
          <button
            type="button"
            onClick={() => umschalten(false)}
            className="protokoll mt-2 normal-case underline decoration-messing/40 underline-offset-4 hover:text-messing"
          >
            Abschalten
          </button>
        )}
      </div>
    )
  }

  const restMs = Math.max(0, runde.faelligAtMs - jetzt)
  const glut = fortschritt(jetzt, runde.gemessenAbMs, runde.faelligAtMs)
  const mm = String(Math.floor(restMs / 60_000)).padStart(2, '0')
  const ss = String(Math.floor((restMs / 1000) % 60)).padStart(2, '0')

  return (
    <div className="flex flex-col items-center border border-eiche bg-bank px-4 py-5 text-center">
      <p className="protokoll">
        §10 · Schnapsuhr · Runde {runde.rundeNr} von {stand.maxRounds}
      </p>
      <UhrZeichen glut={glut} />
      {/* Der Countdown: so lange noch, bis der nächste Schnaps fällig ist. */}
      <p className="mt-2 font-akte text-4xl tabular-nums text-lampe" aria-live="off">
        {restMs === 0 ? 'JETZT' : `${mm}:${ss}`}
      </p>
      <p className="protokoll mt-0.5 normal-case">
        bis zum nächsten Schnaps
        {runde.mode === 'zufallsopfer' ? ' — es trifft zwei Ausgeloste' : ' — es trifft alle'}
      </p>
      <div
        className="mt-3 h-1 w-full max-w-56 overflow-hidden bg-eiche"
        role="meter"
        aria-label="Fortschritt bis zum Schlag"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(glut * 100)}
      >
        <div
          className="h-full bg-gradient-to-r from-messing to-siegel transition-[width] duration-1000"
          style={{ width: `${glut * 100}%` }}
        />
      </div>
      <p className="protokoll mt-2 normal-case">
        Die Länge der Runde wurde zufällig gezogen ({runde.tMinMin}–{runde.tMaxMin} min).
      </p>
      {umschalten && (
        <button
          type="button"
          onClick={() => umschalten(false)}
          className="protokoll mt-2 normal-case underline decoration-messing/40 underline-offset-4 hover:text-messing"
        >
          Abschalten
        </button>
      )}
    </div>
  )
}

/** Ein einzelner Schlag, wie ihn der Strom meldet. */
export interface AlarmSchlag {
  rundeNr?: number
  einheiten?: number
  betroffene?: number[]
}

/**
 * Das Vollbild-Overlay beim Schlag.
 *
 * Schlägt die Uhr mehrmals kurz hintereinander — etwa wenn der Server eine
 * Weile stand und die Kette nachholt —, wird nicht jeder Schlag einzeln
 * weggetippt. Die Kammer fasst zusammen: ein Beschluss, eine Bestätigung.
 *
 * Und es steht darin, wen es trifft. Im Modus „zwei Ausgeloste" bekämen sonst
 * auch die drei Verschonten einen Vollbild-Befehl, der sie nichts angeht.
 */
export function VollstreckungsOverlay({
  schlaege,
  namen,
  eigeneMemberId,
  onSchliessen,
}: {
  schlaege: AlarmSchlag[]
  /** memberId → Anzeigename, für „wen trifft es". */
  namen: Map<number, string>
  eigeneMemberId: number | undefined
  onSchliessen: () => void
}) {
  // Wer den Alarm verpasst, weil das Handy in der Tasche steckt, soll ihn
  // wenigstens spüren. Einmal je Aufzug — nicht je Schlag darin.
  const schlaegt = schlaege.length > 0
  useEffect(() => {
    if (schlaegt && 'vibrate' in navigator) navigator.vibrate?.([120, 60, 240])
  }, [schlaegt])

  if (!schlaegt) return null

  // Nicht jeder Schlag trifft dieselben Leute — was mich trifft, zähle ich
  // über die Schläge, die mich nennen.
  const meine = schlaege.filter(
    (s) => eigeneMemberId !== undefined && (s.betroffene ?? []).includes(eigeneMemberId),
  )
  const meineEinheiten = meine.reduce((summe, s) => summe + (s.einheiten ?? 1), 0)
  const andere = [...new Set(schlaege.flatMap((s) => s.betroffene ?? []))]
    .filter((id) => id !== eigeneMemberId)
    .map((id) => namen.get(id) ?? 'jemand')

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label="Die Schnapsuhr schlägt"
      className="vollstreckung fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 px-6 text-center"
    >
      <p className="protokoll">
        Beschluss der Kammer
        {schlaege.length > 1 && ` · ${schlaege.length} Schläge`}
      </p>
      <span className="stempel stempel-vollstreckt vollstreckung-stempel text-2xl">
        Vollstreckung angeordnet
      </span>

      <div className="max-w-72">
        {meine.length > 0 ? (
          <p className="text-lampe">
            Dich trifft es: <strong>{formatEinheiten(meineEinheiten)}</strong>
            {meine.length > 1 && ` aus ${meine.length} Schlägen`}.
          </p>
        ) : (
          <p className="text-akte">
            {andere.length > 0
              ? `Es trifft ${verbinde(andere)}. Du kommst davon.`
              : 'Die Uhr hat geschlagen.'}
          </p>
        )}
        {meine.length > 0 && andere.length > 0 && (
          <p className="protokoll mt-2 normal-case">Mit dir: {verbinde(andere)}</p>
        )}
      </div>

      <button
        type="button"
        onClick={onSchliessen}
        autoFocus
        className="mt-2 bg-messing px-5 py-2.5 font-sans text-sm font-semibold tracking-wide text-saal uppercase hover:bg-lampe"
      >
        Zur Kenntnis genommen
      </button>
    </div>
  )
}


/** „Leon und Sina" statt „Leon, Sina" — die Kammer spricht ganze Sätze. */
function verbinde(namen: string[]): string {
  if (namen.length <= 1) return namen[0] ?? ''
  return `${namen.slice(0, -1).join(', ')} und ${namen[namen.length - 1]}`
}
